<template>
  <b-modal
    v-bind="{
      ...$attrs,
      cancelDisabled,
      scrollable,
      centered,
      size,
      id,
    }"
    ref="modal"
    :content-class="contentClass || (isMobile ? 'pt-3 pb-2' : 'pt-3')"
    :no-close-on-backdrop="!easyClose || !canCloseOnBackdrop || !closeable"
    :body-class="bodyClass || `pt-2 pb-3 ${isMobile ? 'px-3' : 'px-5'}`"
    :no-close-on-esc="!easyClose || !canCloseOnEsc || !closeable"
    :footer-class="computedFooterClass"
    :dialog-class="{ draggable, 'research-split-modal-dialog': isResearch }"
    :data-cy="dataCy"
    :visible="value"
    @show="$emit('show', $event)"
    @hidden="onHide($event)"
    @open="onOpen($event)"
    @close="onClose"
    @ok="onOk"
  >
    <template #modal-header="{ close }">
      <section :class="['title-section space-x-2 mb-2 mx-3 border-0', { 'header-only-close': headerOnlyClose }]">
        <i v-if="titleIcon" :class="titleIcon" class="fa fa-2x big-icon black"></i>
        <h1 :class="['d-inline-block mb-0 unselectable', { 'ml-3': !!titleIcon }]">
          <slot name="title">{{ title }}</slot>
        </h1>
        <slot name="prepend-close"></slot>
        <b-button-close v-show="closeable" @click="close()">
          <i class="fa fa-square-xmark fa-swap-opacity"></i>
        </b-button-close>
      </section>
      <slot name="subtitle"></slot>
    </template>

    <template #default>
      <slot></slot>
    </template>

    <template #modal-footer>
      <slot name="modal-footer"></slot>
    </template>

    <template #modal-cancel>
      <slot name="modal-cancel"></slot>
    </template>

    <template #modal-ok>
      <slot name="modal-ok"></slot>
    </template>
  </b-modal>
</template>

<script>
  import onComponentRendered from '@/utils/onComponentRendered';
  import uniqid from 'uniqid';

  export default {
    name: 'SplitModal',

    props: {
      id: { type: String, default: uniqid('split-modal-') },
      canCloseOnBackdrop: { type: Boolean, default: true },
      canCloseOnEsc: { type: Boolean, default: true },
      contentClass: { type: String, default: 'pb-2' },
      size: { type: [String, null], default: 'xl' },
      draggable: { type: Boolean, default: true },
      easyClose: { type: Boolean, default: true }, // If set to false, will overwrite canCloseOnEsc and canCloseOnBackdrop
      closeable: { type: Boolean, default: true },
      canClose: { type: Boolean, default: true },
      centered: { type: Boolean, default: true },
      footerClass: { type: String, default: '' },
      titleIcon: { type: String, default: null },
      value: { type: Boolean, default: null },
      noDefaultFooterClass: Boolean,
      headerOnlyClose: Boolean,
      cancelDisabled: Boolean,
      scrollable: Boolean,
      screenSize: String,
      bodyClass: String,
      dataCy: String,
      title: String,
    },

    data: () => ({
      isGrabbed: false,
      pos1: 0,
      pos2: 0,
      pos3: 0,
      pos4: 0,
    }),

    methods: {
      onHide($event) {
        this.$emit('hidden', $event);
        this.$emit('input', false);
      },

      onOpen($event) {
        this.$emit('open', $event);
        this.$emit('input', true);
      },

      onClose() {
        this.$emit('close');
      },

      onOk(event) {
        this.$emit('ok', event);
      },

      hide() {
        this.$refs.modal.hide();
      },

      mousedown(e) {
        const modalHeaderEl = document.querySelector(`#${this.id}___BV_modal_header_`);
        if (this.draggable && (modalHeaderEl.contains(e.target) || e.target === modalHeaderEl)) {
          this.isGrabbed = true;

          this.pos3 = e.clientX;
          this.pos4 = e.clientY;
        }
      },

      mouseup() {
        this.isGrabbed = false;
      },

      mousemove(e) {
        const modalContentEl = this.$refs.modal?.$refs?.modal;
        if (this.isGrabbed && modalContentEl) {
          this.pos1 = this.pos3 - e.clientX;
          this.pos2 = this.pos4 - e.clientY;
          this.pos3 = e.clientX;
          this.pos4 = e.clientY;

          modalContentEl.style.top = modalContentEl.offsetTop - this.pos2 + 'px';
          modalContentEl.style.left = modalContentEl.offsetLeft - this.pos1 + 'px';
        }
      },
    },

    computed: {
      isMobile() {
        return this.$store.state.breakpoint === 'xs';
      },

      computedFooterClass() {
        return `${this.noDefaultFooterClass ? '' : 'py-0 px-3 pt-3 my-3'} ${this.footerClass}`;
      },

      // NOTE: this is a tweak to save time. In the context of a close migration, it makes no sense to find a better way
      isResearch() {
        return this.$route().current().startsWith('research-');
      },
    },

    watch: {
      value: {
        immediate: true,
        handler: function (bool) {
          if (!bool) return;
          onComponentRendered(
            () => document.querySelector(`#${this.id}`),
            (el) => {
              el.addEventListener('mousedown', this.mousedown);
              el.addEventListener('mouseup', this.mouseup);
              el.addEventListener('mousemove', this.mousemove);
            }
          );
        },
      },
    },
  };
</script>
